import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, ShieldCheck, Loader2 } from 'lucide-react';
import PasswordChangeInterceptor from '@/components/auth/PasswordChangeInterceptor';
import { useSupabaseAuth } from '@/hooks/auth';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user, profile, isLoading, refreshUserData } = useSupabaseAuth();

  const handlePasswordChangeSuccess = async () => {
    console.log('✅ Password changed, navigating to dashboard');
    // Navigate immediately - edge function already updated profile
    navigate('/dashboard', { replace: true });
  };

  // Show loading state while auth data is being fetched
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center mb-2">
            <ShieldCheck className="h-6 w-6 text-warning" />
          </div>
          <CardTitle className="text-2xl">Password Change Required</CardTitle>
          <CardDescription>
            For security reasons, you must change your temporary password before continuing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Your temporary password must be changed. Please create a strong password that you'll remember.
            </AlertDescription>
          </Alert>

          <div className="text-sm text-muted-foreground space-y-1 mb-4">
            <p><strong>Account:</strong> {user?.email}</p>
            {profile?.name && <p><strong>Name:</strong> {profile.name}</p>}
          </div>

          <PasswordChangeInterceptor 
            userEmail={user?.email || ''}
            onSuccess={handlePasswordChangeSuccess}
            onError={() => {
              // Silent fail - error already shown via toast in PasswordChangeInterceptor
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePassword;
